import { z } from 'zod';
import { ScenarioExecutionTargetRequestSchema } from './scenario-execution';
import { AutomationTraceModeSchema } from './trace';
import {
  BULK_SCENARIO_PATCH_MAX,
  QualityScenarioSchema,
  SCENARIO_AUTOMATION_STATUS,
  SCENARIO_PRIORITY,
  SCENARIO_STATUS,
} from './scenarios';

export const BULK_SCENARIO_BULK_MAX = BULK_SCENARIO_PATCH_MAX;

export const BULK_COLLECTION_MODE = ['add', 'remove', 'replace'] as const;
export type BulkCollectionMode = (typeof BULK_COLLECTION_MODE)[number];

export const BulkStringCollectionChangeSchema = z.object({
  mode: z.enum(BULK_COLLECTION_MODE),
  values: z.array(z.string().trim().min(1).max(80)).min(1).max(20),
});
export type BulkStringCollectionChange = z.infer<typeof BulkStringCollectionChangeSchema>;

export const BulkRequirementChangeSchema = z
  .object({
    mode: z.enum(BULK_COLLECTION_MODE),
    requirementIds: z.array(z.string().uuid()).max(20),
  })
  .superRefine((value, ctx) => {
    if (value.mode !== 'replace' && value.requirementIds.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one requirement is required',
        path: ['requirementIds'],
      });
    }
  });
export type BulkRequirementChange = z.infer<typeof BulkRequirementChangeSchema>;

export const BulkComponentChangeSchema = z.object({
  mode: z.enum(['add', 'remove']),
  componentIds: z.array(z.string().uuid()).min(1).max(20),
});
export type BulkComponentChange = z.infer<typeof BulkComponentChangeSchema>;

export const BulkTestDataChangeSchema = z.object({
  mode: z.enum(BULK_COLLECTION_MODE),
  dataKeys: z.array(z.string().trim().min(1).max(80)).min(1).max(20),
});
export type BulkTestDataChange = z.infer<typeof BulkTestDataChangeSchema>;

/** Fields included in the request are applied; omitted fields are left unchanged. */
export const BulkScenarioUpdateChangesSchema = z
  .object({
    status: z.enum(SCENARIO_STATUS).optional(),
    priority: z.enum(SCENARIO_PRIORITY).optional(),
    description: z.string().max(2_000).optional(),
    assignedToUserId: z.union([z.string().uuid(), z.null()]).optional(),
    ownerUserId: z.union([z.string().uuid(), z.null()]).optional(),
    businessFlowId: z.string().uuid().optional(),
    automationStatus: z.enum(SCENARIO_AUTOMATION_STATUS).optional(),
    tags: BulkStringCollectionChangeSchema.optional(),
    requirements: BulkRequirementChangeSchema.optional(),
    components: BulkComponentChangeSchema.optional(),
    testData: BulkTestDataChangeSchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, { message: 'At least one change is required' });
export type BulkScenarioUpdateChanges = z.infer<typeof BulkScenarioUpdateChangesSchema>;

export const BulkScenarioAssignChangesSchema = z.object({
  assignedToUserId: z.union([z.string().uuid(), z.null()]),
  ownerUserId: z.union([z.string().uuid(), z.null()]).optional(),
});
export type BulkScenarioAssignChanges = z.infer<typeof BulkScenarioAssignChangesSchema>;

export const BulkScenarioCloneOptionsSchema = z.object({
  keyMode: z.enum(['auto', 'suffix']).default('auto'),
  suffix: z.string().trim().min(1).max(40).optional(),
  copyRequirements: z.boolean().default(true),
  copyComponentReferences: z.boolean().default(true),
  copyTestDataReferences: z.boolean().default(true),
  copyTags: z.boolean().default(true),
});
export type BulkScenarioCloneOptions = z.infer<typeof BulkScenarioCloneOptionsSchema>;

export const BULK_SCENARIO_OPERATIONS = [
  'update',
  'assign',
  'clone',
  'delete',
  'submit_review',
  'approve',
  'request_changes',
  'mark_ready',
  'archive',
  'convert_automation',
  'run_automation',
] as const;
export type BulkScenarioOperation = (typeof BULK_SCENARIO_OPERATIONS)[number];

export const BULK_SCENARIO_OUTCOMES = ['updated', 'skipped', 'failed', 'cloned', 'deleted'] as const;
export type BulkScenarioOutcome = (typeof BULK_SCENARIO_OUTCOMES)[number];

export const BulkScenarioItemResultSchema = z.object({
  scenarioId: z.string().uuid(),
  displayKey: z.string().optional(),
  title: z.string().optional(),
  outcome: z.enum(BULK_SCENARIO_OUTCOMES),
  reason: z.string().optional(),
  scenario: QualityScenarioSchema.optional(),
});
export type BulkScenarioItemResult = z.infer<typeof BulkScenarioItemResultSchema>;

export const BulkScenarioPreviewFieldSchema = z.object({
  field: z.string(),
  currentLabel: z.string(),
  newLabel: z.string(),
  mode: z.enum(BULK_COLLECTION_MODE).optional(),
});
export type BulkScenarioPreviewField = z.infer<typeof BulkScenarioPreviewFieldSchema>;

const scenarioIdsSchema = z.array(z.string().uuid()).min(1).max(BULK_SCENARIO_BULK_MAX);

export const BulkScenarioLifecycleOptionsSchema = z.object({
  comment: z.string().max(2000).optional(),
});
export type BulkScenarioLifecycleOptions = z.infer<typeof BulkScenarioLifecycleOptionsSchema>;

export const BulkScenarioRequestSchema = z.discriminatedUnion('operation', [
  z.object({
    operation: z.literal('update'),
    scenarioIds: scenarioIdsSchema,
    preview: z.boolean().optional(),
    changes: BulkScenarioUpdateChangesSchema,
  }),
  z.object({
    operation: z.literal('assign'),
    scenarioIds: scenarioIdsSchema,
    preview: z.boolean().optional(),
    assign: BulkScenarioAssignChangesSchema,
  }),
  z.object({
    operation: z.literal('clone'),
    scenarioIds: scenarioIdsSchema,
    preview: z.boolean().optional(),
    clone: BulkScenarioCloneOptionsSchema.optional(),
  }),
  z.object({
    operation: z.literal('delete'),
    scenarioIds: scenarioIdsSchema,
    preview: z.boolean().optional(),
  }),
  z.object({
    operation: z.literal('submit_review'),
    scenarioIds: scenarioIdsSchema,
    preview: z.boolean().optional(),
    lifecycle: BulkScenarioLifecycleOptionsSchema.optional(),
  }),
  z.object({
    operation: z.literal('approve'),
    scenarioIds: scenarioIdsSchema,
    preview: z.boolean().optional(),
    lifecycle: BulkScenarioLifecycleOptionsSchema.optional(),
  }),
  z.object({
    operation: z.literal('request_changes'),
    scenarioIds: scenarioIdsSchema,
    preview: z.boolean().optional(),
    lifecycle: z.object({
      comment: z.string().trim().min(1).max(2000),
    }),
  }),
  z.object({
    operation: z.literal('mark_ready'),
    scenarioIds: scenarioIdsSchema,
    preview: z.boolean().optional(),
  }),
  z.object({
    operation: z.literal('archive'),
    scenarioIds: scenarioIdsSchema,
    preview: z.boolean().optional(),
  }),
  z.object({
    operation: z.literal('convert_automation'),
    scenarioIds: scenarioIdsSchema,
    preview: z.boolean().optional(),
  }),
  z.object({
    operation: z.literal('run_automation'),
    scenarioIds: scenarioIdsSchema,
    preview: z.boolean().optional(),
    confirmStale: z.boolean().optional(),
    /** v0.46 — defaults to `auto` when omitted (orchestrator resolves execution target). */
    dispatch: z.literal('auto').optional(),
    executionTarget: ScenarioExecutionTargetRequestSchema.optional(),
    /** v0.46.1 — Playwright trace capture (default off). */
    traceMode: AutomationTraceModeSchema.optional(),
  }),
]);
export type BulkScenarioRequest = z.infer<typeof BulkScenarioRequestSchema>;

export const BulkScenarioResponseSchema = z.object({
  updated: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  cloned: z.number().int().nonnegative().optional(),
  deleted: z.number().int().nonnegative().optional(),
  results: z.array(BulkScenarioItemResultSchema),
  previewFields: z.array(BulkScenarioPreviewFieldSchema).optional(),
});
export type BulkScenarioResponse = z.infer<typeof BulkScenarioResponseSchema>;
